import { useCallback, useEffect, useRef, useState } from 'react';
import { useMountedRef } from './useMountedRef';
import Config from '../../../config';
import {
    ActionItem,
    ActionablePriority,
    OnboardingRow,
    UseOnboardingIntelligenceResult,
    ViewModel,
} from '../onboardingIntelligence.types';
import { PartnerTasks, loadPartnerTasks } from '../partnerTasks';
import { getOnboardingConfig } from '../services/OnboardingIntelligenceService';
import {
    actionableRow,
    buildReasoning,
    classifyScenario,
    computePipeline,
    pickIncompleteTask,
    pickLongestStalled,
} from '../utils/onboardingIntelligenceHelpers';
import { fetchAllODataPages } from '../utils/odata_utils';

const BATCH_SIZE = 5;

const oauthBase = (): string => Config.oauth ?? '';

const onboardingRowsUrl = (): string =>
    `${oauthBase()}/api/odata/integrations?$filter=Priority ne 'Ignored'`;

async function chunkedSettle(
    rows: OnboardingRow[],
    fn: (row: OnboardingRow) => Promise<PartnerTasks | null>
): Promise<Map<string, PartnerTasks | null>> {
    const out = new Map<string, PartnerTasks | null>();
    for (let i = 0; i < rows.length; i += BATCH_SIZE) {
        const batch = rows.slice(i, i + BATCH_SIZE);
        // eslint-disable-next-line no-await-in-loop
        const results = await Promise.allSettled(batch.map((r) => fn(r)));
        results.forEach((res, idx) => {
            const row = batch[idx];
            if (res.status === 'fulfilled') {
                out.set(row.Id, res.value);
            } else {
                out.set(row.Id, null);
            }
        });
    }
    return out;
}

function deriveViewModel(
    rows: OnboardingRow[],
    tasksMap: Map<string, PartnerTasks | null>,
    actionableIds: Set<string>,
    notificationsOn: boolean
): ViewModel {
    const totalSuppliers = rows.length;
    const highPriority = rows.filter((r) => r.Priority === 'High').length;
    const pipeline = computePipeline(rows);

    let unevaluated = 0;
    const actionItems: ViewModel['actionItems'] = [];
    const readyList: ViewModel['readyList'] = [];

    for (const row of rows) {
        if (!actionableIds.has(row.Id)) continue;
        const tasks = tasksMap.get(row.Id);
        if (
            tasks === null &&
            (row.Status === 'Onboarding' || row.Status === 'Go Live Requested')
        ) {
            unevaluated += 1;
        }
        const kind = classifyScenario(row, tasks ?? null);
        if (!kind) continue;

        if (kind === 'scenario1') {
            actionItems.push({
                kind: 'scenario1',
                supplier: {
                    id: row.Id,
                    name: row.CompanyName,
                    email: row.Email,
                    days: row.Days,
                    incompleteTask: pickIncompleteTask(tasks ?? null),
                },
                reasoning: buildReasoning('scenario1', row, tasks ?? null),
            });
        } else if (kind === 'scenario2') {
            if (row.Status === 'Onboarding' || row.Status === 'Go Live Requested') {
                actionItems.push({
                    kind: 'scenario2',
                    supplier: { id: row.Id, name: row.CompanyName, status: row.Status },
                    reasoning: buildReasoning('scenario2', row, tasks ?? null),
                });
                readyList.push({ id: row.Id, companyName: row.CompanyName });
            }
        } else if (kind === 'scenario3') {
            actionItems.push({
                kind: 'scenario3',
                supplier: {
                    id: row.Id,
                    name: row.CompanyName,
                    daysAgo: row.Days ?? 0,
                },
                reasoning: buildReasoning('scenario3', row, null),
            });
        } else {
            // Exhaustiveness guard: adding a 4th ActionItem kind would surface
            // a TS error here. Throwing also catches a hypothetical `'scenario4'`
            // smuggled in from outside the TS surface (untyped JS, network
            // payload) rather than silently dropping the row.
            const _exhaustive: never = kind;
            throw new Error(`Unhandled ActionItem kind: ${String(_exhaustive)}`);
        }
    }

    // sort: S1 → S2 → S3, within-kind days desc
    const kindRank: Record<ActionItem['kind'], number> = { scenario1: 0, scenario2: 1, scenario3: 2 };
    actionItems.sort((a, b) => {
        if (kindRank[a.kind] !== kindRank[b.kind])
            return kindRank[a.kind] - kindRank[b.kind];
        const aDays =
            a.kind === 'scenario1'
                ? a.supplier.days
                : a.kind === 'scenario3'
                  ? a.supplier.daysAgo
                  : 0;
        const bDays =
            b.kind === 'scenario1'
                ? b.supplier.days
                : b.kind === 'scenario3'
                  ? b.supplier.daysAgo
                  : 0;
        return bDays - aDays;
    });

    const readyToGoLive = readyList.length;
    const longestStalled = pickLongestStalled(rows);

    return {
        totalSuppliers,
        readyToGoLive,
        highPriority,
        unevaluated,
        pipeline,
        actionItems,
        readyList,
        longestStalled,
        notificationsOn,
    };
}

export interface UseOnboardingIntelligenceOptions {
    enabled: boolean;
}

export function useOnboardingIntelligence(
    options: UseOnboardingIntelligenceOptions
): UseOnboardingIntelligenceResult {
    const { enabled } = options;
    const [data, setData] = useState<ViewModel | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);
    const [generatedAt, setGeneratedAt] = useState<number>(() => Date.now());

    const requestIdRef = useRef<number>(0);
    const mountedRef = useMountedRef();
    const prevEnabledRef = useRef<boolean>(enabled);
    // refreshNonce is bumped to force the effect to re-run on demand
    const [refreshNonce, setRefreshNonce] = useState<number>(0);

    const refresh = useCallback(() => {
        setRefreshNonce((n) => n + 1);
    }, []);

    useEffect(() => {
        const wasEnabled = prevEnabledRef.current;
        prevEnabledRef.current = enabled;

        if (!enabled) {
            // Don't clear `data` here — the modal's exit animation (~195ms) is
            // still painting the body, and a `null` mid-animation pops content
            // empty. Stale state is cleared on the next close→open edge instead.
            setLoading(false);
            return undefined;
        }

        // Close → open edge: clear stale state so the reopen paints clean
        // loading state before the new fetch resolves.
        if (!wasEnabled) {
            setData(null);
            setError(null);
            setGeneratedAt(Date.now());
        }
        const myRequestId = ++requestIdRef.current;
        setLoading(true);
        setError(null);

        let cancelled = false;

        (async () => {
            try {
                const [rows, config] = await Promise.all([
                    fetchAllODataPages<OnboardingRow>(onboardingRowsUrl()),
                    getOnboardingConfig(),
                ]);
                if (!mountedRef.current || cancelled) return;
                if (requestIdRef.current !== myRequestId) return;

                const actionableRows = rows.filter((r) => actionableRow(r));
                const actionableIds = new Set(actionableRows.map((r) => r.Id));
                const tasksMap = await chunkedSettle(actionableRows, (row) =>
                    loadPartnerTasks({
                        CoId: row.CoId,
                        IntegrationType: row.IntegrationType,
                        ShippingLabelEnabled: Boolean(config?.EnableShippingLabelStep),
                        ShippingLabelStatus: row.ShippingLabelStatus,
                    })
                );
                if (!mountedRef.current || cancelled) return;
                if (requestIdRef.current !== myRequestId) return;

                const vm = deriveViewModel(
                    rows,
                    tasksMap,
                    actionableIds,
                    Boolean(config?.SendOnboardingReminder)
                );
                setData(vm);
                setGeneratedAt(Date.now());
                setLoading(false);
            } catch (err) {
                if (!mountedRef.current || cancelled) return;
                if (requestIdRef.current !== myRequestId) return;
                setError(err instanceof Error ? err : new Error(String(err)));
                setData(null);
                setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
        // `mountedRef` is a stable MutableRefObject from useMountedRef; listing
        // it in deps is a no-op (refs never change identity) but keeps the
        // exhaustive-deps lint coverage intact.
    }, [enabled, refreshNonce, mountedRef]);

    return { data, loading, error, refresh, generatedAt };
}

/** Re-export ActionablePriority for component callers. */
export type { ActionablePriority };
